import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp } from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { BrowserFile } from "@/lib/browser-file";
import { SystemLabels } from "@/lib/labels/system";

import { SectionCard } from "./section-card";

const ACCEPT = ".xls,.xlsx";

export function EtlSection() {
	const inputRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();
	const { data } = useQuery(orpc.etl.summary.queryOptions());

	const [staged, setStaged] = useState<File | null>(null);

	const run = useMutation(
		orpc.etl.run.mutationOptions({
			onSuccess: async (result) => {
				setStaged(null);

				if (result.aborted !== undefined) {
					toast.error(SystemLabels.etl.aborted[result.aborted]);

					return;
				}

				toast.success(SystemLabels.etl.done(result.summary.patients));

				if (result.warnings.length > 0) {
					toast.warning(SystemLabels.etl.warnings(result.warnings.length));
				}

				await queryClient.invalidateQueries();
			},
			onError: () => toast.error(SystemLabels.etl.failed),
		}),
	);

	const onPick = (file: File) => {
		if (!/\.xlsx?$/iu.test(file.name)) {
			toast.error(SystemLabels.etl.unsupported);

			return;
		}

		setStaged(file);
	};

	const confirmRun = async () => {
		if (staged === null) {
			return;
		}

		run.mutate({ data: await BrowserFile.toBase64(staged) });
	};

	return (
		<SectionCard title={SystemLabels.sections.etl} dataComponent="system-etl">
			<div className="flex flex-col gap-3">
				{data !== undefined && data.last !== undefined && (
					<div data-slot="last-run" className="flex flex-col gap-0.5 text-sm text-muted-foreground">
						<span>{SystemLabels.etl.lastRun(data.last.ran_at)}</span>
						<span>{SystemLabels.etl.patients(data.last.patients)}</span>
					</div>
				)}

				{data !== undefined && data.last === undefined && (
					<p data-slot="last-run" className="text-sm text-muted-foreground">
						{SystemLabels.etl.neverRan}
					</p>
				)}

				<input
					ref={inputRef}
					type="file"
					accept={ACCEPT}
					className="hidden"
					onChange={(e) => {
						const file = e.target.files?.[0];

						if (file) {
							onPick(file);
						}

						e.target.value = "";
					}}
				/>

				<div>
					<Button data-slot="etl-upload" onClick={() => inputRef.current?.click()}>
						<FileUp />
						{SystemLabels.etl.action}
					</Button>
				</div>

				<ConfirmationDialog
					tone="destructive"
					title={SystemLabels.etl.selected}
					description={
						<span className="flex flex-col gap-1">
							<span className="break-all font-medium text-foreground">{staged?.name}</span>
							<span>{SystemLabels.etl.confirmHint}</span>
						</span>
					}
					confirmLabel={SystemLabels.etl.confirm}
					pendingLabel={SystemLabels.etl.confirming}
					isPending={run.isPending}
					onConfirm={() => void confirmRun()}
					open={staged !== null}
					onOpenChange={(open) => {
						if (!open) {
							setStaged(null);
						}
					}}
				/>

				<p className="text-xs text-muted-foreground">{SystemLabels.etl.hint}</p>
			</div>
		</SectionCard>
	);
}
